/**
 * useAdvisorCompliance — advisor-side Phase 3.5 derivation hook.
 *
 * One place that turns the raw compliance sideload on the contacts
 * response (`has_compliance_reviewers` + `compliance_field_map`) plus the
 * already-attached decision blobs on `contact.custom_fields[field_id]`
 * into everything the advisor UI needs:
 *
 *   1. `getDecision(contact, draftConfigId)` — the canonical per-(contact,
 *      draft) verdict the chip renderer consumes. Returns null when
 *      there's no decision row yet (renders as "Not reviewed").
 *   2. `summary` — campaign-level counts for the summary strip. Zeros
 *      out cleanly when compliance UI is disabled.
 *   3. Filter state + matcher, so clicking a summary chip filters the
 *      contacts list to just the matching rows.
 *   4. `openNonCompliantModal` / `modalContext` plumbing so clicking a
 *      red chip anywhere in the table opens the single shared diff modal
 *      with the right snapshot.
 *
 * Deliberately contains ZERO backend awareness beyond the shape of the
 * decision payload documented in `backend/api/reviews_api.py` — the hook
 * is a pure function of its inputs plus a small amount of local state
 * (filter + modal + client-side hashes). Testing it is trivial.
 *
 * Staleness — two layers, server-first:
 *
 *   Layer 1 (source of truth, first paint): the backend stamps
 *   `decision.is_stale` on every contacts GET via
 *   `_enrich_advisor_contacts_with_compliance` using the shared
 *   `utils/compliance_hashing.py` formula. This value is what reaches
 *   the chip before the advisor has touched anything.
 *
 *   Layer 2 (live overlay, post-edit): once the advisor edits subject
 *   or body, the raw contacts snapshot on which layer 1 lives is NOT
 *   refetched (the table uses imperative `fetchTabData`, not React
 *   Query), so the server-stamped verdict goes stale about its own
 *   staleness. To close that gap, this hook re-runs the *same* hash
 *   formula on the client via
 *   `frontend/src/utils/complianceHash.js` — a passive port of the
 *   Python spec — and overlays `is_stale` onto the returned decision
 *   when the client hash has been computed. The formula is documented
 *   on the server; the client is just a second caller, not a divergent
 *   implementation. If Web Crypto ever rejects, the overlay silently
 *   falls back to the server value and the chip behaves exactly as it
 *   did before this layer was added.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { computeComplianceContentHash } from '../utils/complianceHash';
import {
  COMPLIANCE_FILTERS,
  EMPTY_COMPLIANCE_SUMMARY,
  classifyComplianceForSummary,
  matchesComplianceFilter,
} from '../utils/complianceFilters';

export const ADVISOR_COMPLIANCE_FILTERS = COMPLIANCE_FILTERS;

const EMPTY_LIST = Object.freeze([]);

/**
 * @param {object} params
 * @param {Array<object>} params.contacts       Raw contacts from the contacts API response.
 *                                              Must carry `membership_id` and the server-attached
 *                                              `custom_fields` map (pre-client-enrichment), since
 *                                              compliance decision blobs are deliberately excluded
 *                                              from the advisor `field_definitions` list and get
 *                                              stripped by `useCustomFields.enrichEntityData`.
 * @param {Array<object>} [params.enrichedContacts]  Same contacts *after* `enrichEntityData` —
 *                                                   carries live subject/body values (including
 *                                                   unsaved optimistic edits). Used only to feed
 *                                                   the client-side staleness overlay. When
 *                                                   omitted, the hook falls back to the
 *                                                   server-stamped `is_stale` value unchanged,
 *                                                   which preserves pre-overlay behavior.
 * @param {Array<object>} params.messageDrafts  All message drafts for the campaign. Each entry
 *                                              should carry `id` (config_id), `subjectFieldId`,
 *                                              and `bodyFieldId` so the overlay can locate the
 *                                              current content for hashing.
 * @param {Record<string,string>} params.complianceFieldMap  config_id -> compliance field definition id.
 * @param {boolean} params.hasComplianceReviewers  Master switch for the compliance UI.
 */
const useAdvisorCompliance = ({
  contacts,
  enrichedContacts,
  messageDrafts,
  complianceFieldMap,
  hasComplianceReviewers,
}) => {
  const [filter, setFilter] = useState(ADVISOR_COMPLIANCE_FILTERS.ALL);
  const [modalContext, setModalContext] = useState(null);

  const safeFieldMap = complianceFieldMap || {};
  const safeContacts = useMemo(
    () => (Array.isArray(contacts) ? contacts : EMPTY_LIST),
    [contacts],
  );
  const safeEnrichedContacts = useMemo(
    () => (Array.isArray(enrichedContacts) ? enrichedContacts : EMPTY_LIST),
    [enrichedContacts],
  );
  const safeDrafts = useMemo(
    () => (Array.isArray(messageDrafts) ? messageDrafts : EMPTY_LIST),
    [messageDrafts],
  );

  /**
   * Build a membership_id -> raw custom_fields lookup once per contacts
   * change. Callers (TableRow, summary counter, filter matcher) pass the
   * *enriched* contact object they have in scope, which has had its
   * `custom_fields` rebuilt from the advisor-visible `field_definitions`
   * list — a list that deliberately excludes `source='compliance'`
   * definitions. The raw server response on `contactsData` still carries
   * the decision blobs, so we index into that shadow copy by
   * membership_id rather than trusting whatever shape the enrichment
   * pipeline leaves on the passed-in contact.
   */
  const customFieldsByMembership = useMemo(() => {
    const lookup = new Map();
    for (const contact of safeContacts) {
      const key = contact?.membership_id;
      if (!key) continue;
      lookup.set(String(key), contact.custom_fields || {});
    }
    return lookup;
  }, [safeContacts]);

  /**
   * Mirror lookup over the *enriched* contacts, so the staleness
   * overlay can read the live (post-edit) subject / body values by
   * field id. Deliberately kept separate from `customFieldsByMembership`
   * — the raw map owns decision blobs (stripped during enrichment),
   * this map owns live content (rebuilt during enrichment). Merging
   * them would conflate two different freshness contracts.
   */
  const enrichedCustomFieldsByMembership = useMemo(() => {
    const lookup = new Map();
    for (const contact of safeEnrichedContacts) {
      const key = contact?.membership_id;
      if (!key) continue;
      lookup.set(String(key), contact.custom_fields || {});
    }
    return lookup;
  }, [safeEnrichedContacts]);

  /**
   * `${membership_id}|${config_id}` -> SHA-256 hex of the CURRENT
   * `(subject, body)` pair, computed on the client via the same formula
   * the server uses. Empty until the first effect pass completes; at
   * that point the overlay kicks in and `getDecision` flips `is_stale`
   * whenever this client hash disagrees with the decision's stored
   * `reviewed_content_hash`.
   */
  const [currentContentHashes, setCurrentContentHashes] = useState(() => new Map());

  /**
   * Guards against out-of-order async settlement: every effect run bumps
   * this counter, and the async body refuses to publish its results if
   * a newer run has already been scheduled. Cheaper + simpler than
   * wiring AbortController through a multi-promise fan-out.
   */
  const hashRunIdRef = useRef(0);

  useEffect(() => {
    if (!hasComplianceReviewers) {
      // Disabled path: make sure any stale overlay from a previous
      // "reviewers present" session doesn't linger and mislead the chip.
      setCurrentContentHashes((prev) => (prev.size === 0 ? prev : new Map()));
      return undefined;
    }
    if (customFieldsByMembership.size === 0 || safeDrafts.length === 0) {
      setCurrentContentHashes((prev) => (prev.size === 0 ? prev : new Map()));
      return undefined;
    }

    hashRunIdRef.current += 1;
    const runId = hashRunIdRef.current;
    let cancelled = false;

    const run = async () => {
      /**
       * Collect (key, subject, body) for every (contact, draft) pair
       * that carries a decision with a `reviewed_content_hash`. Pairs
       * without a stored hash have nothing to compare against — they
       * stay on the server-stamped `is_stale` (typically the pre-3.4
       * legacy "always stale" path).
       */
      const jobs = [];
      for (const [membershipId, rawCustomFields] of customFieldsByMembership) {
        const liveCustomFields = enrichedCustomFieldsByMembership.get(membershipId);
        for (const draft of safeDrafts) {
          if (!draft || !draft.id) continue;
          const configKey = String(draft.id);
          const complianceFieldId = safeFieldMap[configKey];
          if (!complianceFieldId) continue;
          const decision = rawCustomFields?.[complianceFieldId];
          if (!decision || typeof decision !== 'object') continue;
          if (!decision.reviewed_content_hash) continue;

          // Prefer the enriched (live) value; fall back to the raw
          // server value so the overlay stays useful even when the
          // caller hasn't wired `enrichedContacts` yet.
          const subjectFieldId = draft.subjectFieldId;
          const bodyFieldId = draft.bodyFieldId;
          const subject = subjectFieldId
            ? (liveCustomFields?.[subjectFieldId] ?? rawCustomFields?.[subjectFieldId])
            : undefined;
          const body = bodyFieldId
            ? (liveCustomFields?.[bodyFieldId] ?? rawCustomFields?.[bodyFieldId])
            : undefined;

          jobs.push({
            key: `${membershipId}|${configKey}`,
            subject,
            body,
          });
        }
      }

      if (jobs.length === 0) {
        if (cancelled || runId !== hashRunIdRef.current) return;
        setCurrentContentHashes((prev) => (prev.size === 0 ? prev : new Map()));
        return;
      }

      let hashes;
      try {
        hashes = await Promise.all(
          jobs.map((job) => computeComplianceContentHash(job.subject, job.body)),
        );
      } catch (err) {
        // Web Crypto unavailable (non-secure context, very old jsdom,
        // locked-down browser, etc.). Log once and leave the overlay
        // empty — the chip silently falls back to server staleness.
        if (!cancelled && runId === hashRunIdRef.current) {
          // eslint-disable-next-line no-console
          console.warn(
            '[useAdvisorCompliance] Falling back to server-stamped is_stale; client hashing failed:',
            err,
          );
        }
        return;
      }

      if (cancelled || runId !== hashRunIdRef.current) return;

      setCurrentContentHashes((prev) => {
        /**
         * Equality short-circuit: if every key still maps to the same
         * hex digest and the set of keys hasn't changed, keep the
         * previous Map identity. Prevents a no-op re-render of every
         * consumer (the chip, the summary strip, the filter matcher)
         * on every keystroke that happens to hash to the same value
         * (think: the advisor typing and un-typing a character).
         */
        if (prev.size === jobs.length) {
          let identical = true;
          for (let i = 0; i < jobs.length; i += 1) {
            if (prev.get(jobs[i].key) !== hashes[i]) {
              identical = false;
              break;
            }
          }
          if (identical) return prev;
        }
        const next = new Map();
        for (let i = 0; i < jobs.length; i += 1) {
          next.set(jobs[i].key, hashes[i]);
        }
        return next;
      });
    };

    run();

    return () => {
      cancelled = true;
    };
    // `safeFieldMap` is a new object literal each render; depending on
    // `complianceFieldMap` (its source) keeps the dep list stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    hasComplianceReviewers,
    customFieldsByMembership,
    enrichedCustomFieldsByMembership,
    safeDrafts,
    complianceFieldMap,
  ]);

  /**
   * Synchronous decision lookup. Extracts from the shadow
   * `custom_fields` map keyed by membership_id — no fetching, no
   * promises, safe to call from the render body of a virtualized row.
   *
   * Applies the client-side staleness overlay when available: if we've
   * already hashed the current `(subject, body)` for this row, use that
   * hash vs the decision's stored `reviewed_content_hash` as the
   * authoritative `is_stale` — that's what lets the chip flip the
   * moment the advisor edits content, instead of waiting for the next
   * contacts GET. We only diverge from the server value when the hashes
   * actually disagree on the stale bit; otherwise we return the raw
   * reference unchanged so downstream memoization stays hot.
   */
  const getDecision = useCallback(
    (contact, draftConfigId) => {
      if (!hasComplianceReviewers || !contact || !draftConfigId) return null;
      const fieldId = safeFieldMap[String(draftConfigId)];
      if (!fieldId) return null;
      const membershipId = contact.membership_id;
      if (!membershipId) return null;
      const rawCustomFields = customFieldsByMembership.get(String(membershipId));
      const raw = rawCustomFields?.[fieldId];
      if (!raw || typeof raw !== 'object') return null;

      const reviewedHash = raw.reviewed_content_hash;
      if (!reviewedHash) return raw;
      const liveHash = currentContentHashes.get(`${membershipId}|${draftConfigId}`);
      if (!liveHash) return raw;
      const liveStale = liveHash !== reviewedHash;
      if (Boolean(raw.is_stale) === liveStale) return raw;
      return { ...raw, is_stale: liveStale };
    },
    // `safeFieldMap` is derived from `complianceFieldMap` each render,
    // but listing the source dependency avoids spurious identity churn.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasComplianceReviewers,
      complianceFieldMap,
      customFieldsByMembership,
      currentContentHashes,
    ],
  );

  /**
   * Does at least one (contact, draft) pair on this row match the active
   * filter? Used by the table to hide rows that have no matching chips
   * when a summary filter is active. "All" is a no-op.
   */
  const contactMatchesFilter = useCallback(
    (contact) => {
      if (filter === ADVISOR_COMPLIANCE_FILTERS.ALL) return true;
      if (!hasComplianceReviewers) return true;
      for (const draft of safeDrafts) {
        const decision = getDecision(contact, draft.id);
        if (matchesComplianceFilter(decision, filter)) return true;
      }
      return false;
    },
    [filter, hasComplianceReviewers, safeDrafts, getDecision],
  );

  /**
   * Campaign-wide totals, computed in a single pass over the contacts *
   * drafts cross product. Cheap: both arrays cap out in the hundreds in
   * practice, and a decision lookup is a dict hit.
   *
   * Zeroed when compliance UI is disabled so callers can branch on
   * `summary.total === 0` without a separate flag.
   */
  const summary = useMemo(() => {
    if (!hasComplianceReviewers) return EMPTY_COMPLIANCE_SUMMARY;
    let compliant = 0;
    let nonCompliant = 0;
    let needsHumanReview = 0;
    let notReviewed = 0;
    let stale = 0;
    let staleCompliant = 0;
    let staleNonCompliant = 0;
    let staleNeedsHuman = 0;
    for (const contact of safeContacts) {
      for (const draft of safeDrafts) {
        const decision = getDecision(contact, draft.id);
        const bucket = classifyComplianceForSummary(decision);
        if (bucket === 'compliant') compliant += 1;
        else if (bucket === 'nonCompliant') nonCompliant += 1;
        else if (bucket === 'needsHumanReview') needsHumanReview += 1;
        else if (bucket === 'stale') {
          stale += 1;
          if (decision?.status === COMPLIANCE_FILTERS.NON_COMPLIANT) staleNonCompliant += 1;
          else if (decision?.status === COMPLIANCE_FILTERS.NEEDS_HUMAN_REVIEW)
            staleNeedsHuman += 1;
          else if (decision?.status === COMPLIANCE_FILTERS.COMPLIANT) staleCompliant += 1;
        } else notReviewed += 1;
      }
    }
    const total = compliant + nonCompliant + needsHumanReview + notReviewed + stale;
    return {
      total,
      compliant,
      nonCompliant,
      needsHumanReview,
      notReviewed,
      stale,
      staleCompliant,
      staleNonCompliant,
      staleNeedsHuman,
    };
  }, [hasComplianceReviewers, safeContacts, safeDrafts, getDecision]);

  /**
   * Open the shared non-compliant modal for one (contact, draft) pair.
   * We capture the data needed to render the diff at click time — the
   * stored reviewed_subject / reviewed_body from the decision, plus the
   * current resolved subject / body the caller already has in scope —
   * so the modal doesn't need to re-derive anything itself.
   */
  const openNonCompliantModal = useCallback((ctx) => {
    if (!ctx) return;
    setModalContext(ctx);
  }, []);

  const closeNonCompliantModal = useCallback(() => {
    setModalContext(null);
  }, []);

  return {
    /* master switch */
    isEnabled: Boolean(hasComplianceReviewers),
    /* per-(contact, draft) lookup */
    getDecision,
    /* campaign-wide summary counts for the strip */
    summary,
    /* clickable filter state */
    filter,
    setFilter,
    contactMatchesFilter,
    /* non-compliant diff modal */
    modalContext,
    openNonCompliantModal,
    closeNonCompliantModal,
  };
};

export default useAdvisorCompliance;
